/**
 * Unified Analytics Interface — the ONLY analytics surface app code imports.
 *
 * ```ts
 * import { trackEvent, logError } from '@/modules/analytics';
 * ```
 *
 * ## What this file is now
 *
 * A thin, **vendor-agnostic facade over one injected port** (`AnalyticsPort`). It used to
 * import `libs/posthog` and `libs/datadog` directly and fan each call out to both. That
 * fan-out has moved into the adapter that owns those vendors
 * (`#modules/vidaVendors`), for two reasons:
 *
 * 1. **Correctness.** Inside a host that already runs PostHog against the same project,
 *    constructing our own client splits one user's identity in two — see `AnalyticsPort`.
 *    A host injects its own and our events land on its identity, its session, its
 *    Datadog service.
 * 2. **Packaging.** Those imports made `posthog-js`, `posthog-react-native` and the four
 *    `@datadog/*` packages hard requirements of anything reaching this module, and Metro
 *    does not tree-shake, so an unused import is still evaluated. Severing them here is
 *    what lets this graph be published at all — `src/packaging.test.ts` asserts the core
 *    reaches no vendor SDK.
 *
 * Every function keeps its previous signature, so no call site changed. With nothing
 * published they no-op (and flag reads resolve to their documented defaults), which is the
 * same behaviour the old code had when PostHog was disabled.
 *
 * See `docs/vendor-ownership-proposal.md`.
 */
import type { User } from '@/types/user';

import type { AnalyticsPort, AppOpenSource } from '../types/analyticsPort';

import { guard } from './guard';

/**
 * The injected port — whatever `OnboardingRoot` was handed, or nothing.
 *
 * Kept here beside the facade rather than in its own module, mirroring
 * `api/apiClient.ts`, which holds `injectedApiClient` next to the delegating `apiClient`.
 * The slot and the thing that reads it are one concern.
 *
 * Module-scoped mutable state, and deliberately **not** a React context. A context was the
 * obvious first design and it cannot serve the readers that exist: ~44 call sites import
 * `@/modules/analytics` as plain functions, and several are not components at all
 * (`maybeRedirectToWebApp.ts`, the api interceptors). Adding a context *as well* would mean
 * two ways to reach one port and a rule about which to use where.
 *
 * The cost a context would buy — re-rendering when it changes — is worth nothing: it is
 * published once during `OnboardingRoot`'s render, before children commit, and never changes
 * for the life of the process. Nothing needs to react to it.
 *
 * Unset, every member is absent and the facade below no-ops. That is the correct default
 * rather than an error: a host that doesn't care about onboarding telemetry passes nothing,
 * exactly as `dynamicAssessments` already allows.
 */
let injectedAnalytics: AnalyticsPort = {};

/**
 * Publish the port. Called by `OnboardingRoot` **during render**, not from an effect — an
 * effect fires after children commit, which is late enough for a boot-time `identifyUser` or
 * a first `$pageview` to have been dropped on the floor.
 *
 * Idempotent by nature (a plain assignment), so re-renders and StrictMode's double invoke are
 * harmless.
 *
 * Passing `undefined` clears it, which is what a caller that supplies no `analytics` does —
 * worth being explicit about, because the alternative (leaving a previous one in place) would
 * let one mount's host port leak into another's.
 */
export const setAnalytics = (next: AnalyticsPort | undefined): void => {
  injectedAnalytics = next ?? {};
};

/**
 * Test-only reset. The slot is process-wide, so a test that publishes one would otherwise
 * leak into every test after it in the same worker.
 */
export const resetAnalytics = (): void => {
  injectedAnalytics = {};
};

/**
 * Read the port. Called per invocation rather than captured, so a call that happens to run
 * before `OnboardingRoot` rendered sees the empty default and no-ops instead of holding a
 * stale reference to it forever.
 */
export const getAnalytics = (): AnalyticsPort => injectedAnalytics;

/** Bind telemetry to a known user (login). */
const identifyUser = async (user: User): Promise<void> => {
  guard('identifyUser', () => getAnalytics().identifyUser?.(user));
};

/** Unbind (logout / 401). */
const resetUser = async (): Promise<void> => {
  guard('resetUser', () => getAnalytics().resetUser?.());
};

const trackEvent = (eventName: string, properties?: Record<string, any>): void => {
  guard('trackEvent', () => getAnalytics().trackEvent?.(eventName, properties));
};

const trackScreen = async (screenName: string, properties?: Record<string, any>): Promise<void> => {
  guard('trackScreen', () => getAnalytics().trackScreen?.(screenName, properties));
};

/**
 * The step-change counterpart to `trackScreen`, for ports that must not start a RUM view.
 *
 * Deliberately absent from the `analytics` aggregate below: that object mirrors
 * VidaReactNative's own `analytics` export so cross-repo-synced modules can share a body, and
 * VRN has no `trackStep`. Adding it there would imply a parity that does not exist.
 */
const trackStep = async (stepName: string, properties?: Record<string, any>): Promise<void> => {
  guard('trackStep', () => getAnalytics().trackStep?.(stepName, properties));
};

const trackError = async (error: Error | string, context?: Record<string, any>): Promise<void> => {
  guard('trackError', () => getAnalytics().trackError?.(error, context));
};

/**
 * Log an operational error to Datadog Logs — the migration equivalent of Sparta's
 * `logErrorMessage`. Distinct from `trackError`, which records a RUM error plus a PostHog
 * `error` event. Use this for errors you monitor / alert on via Datadog Logs.
 *
 * The `__DEV__` console mirror stays on this side of the port rather than moving into the
 * adapter: it is the local-development ergonomic contributors rely on, and it should hold
 * whatever port a host injected — including none at all, which is exactly when a silent
 * `logError` is most confusing.
 */
const logError = async (message: string, context?: Record<string, any>): Promise<void> => {
  if (__DEV__) {
    console.error(message, context);
  }
  guard('logError', () => getAnalytics().logError?.(message, context));
};

/**
 * Log an expected, non-fault event to Datadog Logs — the equivalent of Sparta's
 * `Datadog.sendInfo`. Use for outcomes worth recording but that aren't errors (e.g. an
 * existing-account collision), so they don't inflate error-level alerts.
 */
const logInfo = async (message: string, context?: Record<string, any>): Promise<void> => {
  if (__DEV__) {
    console.info(message, context);
  }
  guard('logInfo', () => getAnalytics().logInfo?.(message, context));
};

const setUserProperties = async (properties: Record<string, any>): Promise<void> => {
  guard('setUserProperties', () => getAnalytics().setUserProperties?.(properties));
};

/**
 * Set Datadog-only global-context properties (Sparta's `Datadog.setGlobalContext`) — for
 * context that belongs on Datadog RUM/Logs but not as a PostHog person property, e.g.
 * `programRecommendationType`.
 */
const setDatadogGlobalContext = async (properties: Record<string, any>): Promise<void> => {
  guard('setDatadogGlobalContext', () => getAnalytics().setDatadogGlobalContext?.(properties));
};

const removeUserProperty = async (key: string): Promise<void> => {
  guard('removeUserProperty', () => getAnalytics().removeUserProperty?.(key));
};

const trackAppOpen = (source: AppOpenSource = 'fresh_open'): void => {
  guard('trackAppOpen', () => getAnalytics().trackAppOpen?.(source));
};

/**
 * Feature flags come from PostHog, so whoever owns that client owns the flags. Embedded,
 * they resolve against the host's identity — the behaviour you want, provided it is the
 * same PostHog project. See `AnalyticsPort.onFeatureFlags`.
 *
 * The unset defaults match what the old code returned when PostHog was disabled:
 * `undefined`, `false`, and a noop unsubscribe. `useFeatureFlag` treats `undefined` as
 * "still resolving" and `false` as "off", so failing closed here is the safe direction.
 */
const getFeatureFlag = async (key: string): Promise<string | boolean | undefined> => {
  try {
    return await getAnalytics().getFeatureFlag?.(key);
  } catch (error) {
    console.error('Analytics: getFeatureFlag failed', error);
    return undefined;
  }
};

const isFeatureFlagEnabled = async (key: string): Promise<boolean> => {
  try {
    return (await getAnalytics().isFeatureFlagEnabled?.(key)) ?? false;
  } catch (error) {
    console.error('Analytics: isFeatureFlagEnabled failed', error);
    return false;
  }
};

const onFeatureFlags = (callback: () => void): (() => void) => {
  try {
    return getAnalytics().onFeatureFlags?.(callback) ?? (() => {});
  } catch (error) {
    console.error('Analytics: onFeatureFlags failed', error);
    return () => {};
  }
};

// Named exports for convenience
export {
  identifyUser,
  resetUser,
  trackEvent,
  trackScreen,
  trackStep,
  trackError,
  logError,
  logInfo,
  trackAppOpen,
  setUserProperties,
  setDatadogGlobalContext,
  removeUserProperty,
  getFeatureFlag,
  onFeatureFlags,
  isFeatureFlagEnabled,
};

/**
 * Aggregate analytics object — mirrors VidaReactNative's `analytics` export
 * (`@/utils/analytics`) so cross-repo-synced modules (e.g. `useAddressVerification`) can
 * call `analytics.trackEvent(...)` / `analytics.trackError(...)` with an identical body in
 * both repos. See SYNC.md.
 *
 * It is also the shape a host implements: VRN's own `analytics` object already satisfies
 * 12 of `AnalyticsPort`'s 14 members, so wiring it up is close to passing this verbatim
 * in the other direction.
 */
export const analytics = {
  identifyUser,
  resetUser,
  trackEvent,
  trackScreen,
  trackError,
  logError,
  logInfo,
  trackAppOpen,
  setUserProperties,
  setDatadogGlobalContext,
  removeUserProperty,
  getFeatureFlag,
  isFeatureFlagEnabled,
};
